import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConflictResolution = ({ tenantId }) => {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);

  useEffect(() => {
    if (tenantId) {
      loadConflicts();
    }
  }, [tenantId]);

  const loadConflicts = async () => {
    setLoading(true);
    try {
      // Identify products with potential conflicts (negative stock, mismatched logs, etc.)
      const { data, error } = await supabase
        ?.from('pos_products')
        ?.select('*, pos_categories(name)')
        ?.eq('tenant_id', tenantId)
        ?.or('stock_quantity.lt.0,stock_quantity.is.null');

      if (error) throw error;

      // Transform into conflict objects
      const conflictData = data?.map(product => ({
        id: product?.id,
        productName: product?.name,
        sku: product?.sku,
        category: product?.pos_categories?.name,
        conflictType: product?.stock_quantity < 0 ? 'negative_stock' : 'null_stock',
        currentStock: product?.stock_quantity,
        expectedStock: 0,
        severity: product?.stock_quantity < 0 ? 'high' : 'medium',
        detectedAt: new Date()?.toISOString()
      })) || [];

      setConflicts(conflictData);
    } catch (error) {
      console.error('Load conflicts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveConflict = async (conflict, resolution) => {
    setResolving(conflict?.id);
    try {
      let newStock = 0;
      
      if (resolution === 'reset') {
        newStock = 0;
      } else if (resolution === 'manual') {
        const input = prompt('Enter correct stock quantity:');
        if (input === null) {
          setResolving(null);
          return;
        }
        newStock = parseInt(input) || 0;
      }

      // Update product stock
      const { error: updateError } = await supabase
        ?.from('pos_products')
        ?.update({ stock_quantity: newStock })
        ?.eq('id', conflict?.id);

      if (updateError) throw updateError;

      // Log the resolution
      const { error: logError } = await supabase
        ?.from('pos_inventory_logs')
        ?.insert({
          tenant_id: tenantId,
          product_id: conflict?.id,
          quantity_change: newStock - (conflict?.currentStock || 0),
          quantity_before: conflict?.currentStock || 0,
          quantity_after: newStock,
          reason: 'adjustment',
          notes: `Conflict resolved: ${conflict?.conflictType} - ${resolution}`
        });

      if (logError) throw logError;

      // Remove from conflicts list
      setConflicts(prev => prev?.filter(c => c?.id !== conflict?.id));
    } catch (error) {
      console.error('Resolve conflict error:', error);
      alert('Failed to resolve conflict');
    } finally {
      setResolving(null);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-danger';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-danger/10';
      case 'medium':
        return 'bg-warning/10';
      case 'low':
        return 'bg-primary/10';
      default:
        return 'bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Conflict Resolution</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Identify and resolve inventory discrepancies across systems
          </p>
        </div>
        <Button variant="outline" onClick={loadConflicts}>
          <Icon name="RefreshCw" size={18} className="mr-2" />
          Scan for Conflicts
        </Button>
      </div>

      {/* Conflicts List */}
      <div className="bg-card rounded-lg border border-border">
        {loading ? (
          <div className="p-12 text-center">
            <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Scanning for conflicts...</p>
          </div>
        ) : conflicts?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="CheckCircle" size={64} className="text-success mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Conflicts Detected</h3>
            <p className="text-muted-foreground">All inventory data is synchronized correctly</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {conflicts?.map((conflict) => (
              <div key={conflict?.id} className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${getSeverityBg(conflict?.severity)}`}>
                    <Icon
                      name="AlertTriangle"
                      size={24}
                      className={getSeverityColor(conflict?.severity)}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">
                          {conflict?.productName}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          SKU: {conflict?.sku} • Category: {conflict?.category}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        getSeverityBg(conflict?.severity)
                      } ${getSeverityColor(conflict?.severity)}`}>
                        {conflict?.severity?.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground block mb-1">Conflict Type</span>
                          <span className="font-medium text-foreground">
                            {conflict?.conflictType?.replace('_', ' ')?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block mb-1">Current Stock</span>
                          <span className="font-medium text-danger">
                            {conflict?.currentStock ?? 'NULL'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block mb-1">Expected Stock</span>
                          <span className="font-medium text-success">
                            {conflict?.expectedStock}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        size="sm"
                        onClick={() => handleResolveConflict(conflict, 'reset')}
                        disabled={resolving === conflict?.id}
                      >
                        {resolving === conflict?.id ? (
                          <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                        ) : (
                          <Icon name="RotateCcw" size={16} className="mr-2" />
                        )}
                        Reset to Zero
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolveConflict(conflict, 'manual')}
                        disabled={resolving === conflict?.id}
                      >
                        <Icon name="Edit" size={16} className="mr-2" />
                        Manual Override
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConflictResolution;